import { Appointment } from "core/domain/booking/BookingState";
import { BookingStep, Move } from "core/domain/booking/Enums";
import { Practice } from "core/domain/booking/Practice";
import { ProviderOnlineStatusCodes } from "modules/bhb/pages/select-appointment-slot/types.js";

import { ConfirmedReservation } from "../../core/domain/booking/booking.types";
import {
  AppointmentTypeDto,
  ProviderDto
} from "../../gateway/clients/bhb/bhb.dtos";

export interface BookingWizardStepOptions {
  practice?: Practice;
  isNewPatient?: boolean;
  isAppointmentForUser?: boolean;
  dependantId?: string;
  reservation?: ConfirmedReservation;
  appointment?: Appointment;
  appointmentTypes?: AppointmentTypeDto[];
  providers?: ProviderDto[];
  rescheduleAppointmentId?: string;
  isUserProfileComplete?: boolean;
  isPatientNotesEnabled: boolean;
  isTelehealthConsult: boolean;
  step: BookingStep;
  hasAppointmentTypeInformation?: boolean;
}

export class BookingWizardStepHelper {
  /**
   * Get the logical "current" step based on what information has been filled out in the booking wizard.
   */
  public calculateLogicalStep(
    requestedStep: BookingStep,
    options: BookingWizardStepOptions
  ): BookingStep {
    const {
      practice,
      isNewPatient,
      isAppointmentForUser,
      dependantId,
      reservation,
      appointment,
      appointmentTypes,
      providers,
      rescheduleAppointmentId,
      isUserProfileComplete,
      isPatientNotesEnabled,
      isTelehealthConsult
    } = options;

    // Check for Practice Outage
    if (practice?.bookingSettings.practiceOutage) {
      return BookingStep.PracticeOutage;
    }

    // immediately check if only booking are enabled and practice has at least one assign appointment type
    // Ensure not all providers are set to Call Clinic
    if (
      !practice?.bookingSettings.isEnabled ||
      !appointmentTypes?.length ||
      appointmentTypes?.every(at => at.callClinicEnabled) ||
      !providers?.length ||
      providers.every(
        x => x.providerOnlineStatus === ProviderOnlineStatusCodes.call
      )
    )
      return BookingStep.NotAcceptingBookings;

    if (
      requestedStep === BookingStep.SelectAppointmentSlot &&
      rescheduleAppointmentId
    ) {
      return BookingStep.SelectAppointmentSlot;
    }

    if (requestedStep === BookingStep.NotAcceptingNewPatients) {
      return BookingStep.NotAcceptingNewPatients;
    }

    if (
      requestedStep === BookingStep.NotAcceptingBookings &&
      practice.bookingSettings.isEnabled
    ) {
      return BookingStep.NotAcceptingBookings;
    }

    const hasPatient = !!(isAppointmentForUser || dependantId);

    if (
      requestedStep > BookingStep.SelectPatientType &&
      isNewPatient === false && // Using strict equality as this will be undefined on the BookingSuccess page after it calls resetWizardState
      appointmentTypes.filter(x => x.isAvailableExistingPatients === true)
        .length === 0
    ) {
      return BookingStep.NotAcceptingBookings;
    }

    if (
      requestedStep > BookingStep.SelectPatientType &&
      isNewPatient &&
      appointmentTypes.filter(x => x.isAvailableNewPatients === true).length ===
        0
    ) {
      return BookingStep.NotAcceptingNewPatients;
    }

    if (isUserProfileComplete) {
      if (requestedStep === BookingStep.BookingSuccess && appointment)
        return BookingStep.BookingSuccess;
      if (requestedStep >= BookingStep.ConfirmBooking && hasPatient)
        return BookingStep.ConfirmBooking;
      if (requestedStep === BookingStep.AddDependant && !!reservation)
        return BookingStep.AddDependant;
      if (
        requestedStep === BookingStep.AddPatientNotes &&
        !!reservation &&
        isPatientNotesEnabled
      )
        return BookingStep.AddPatientNotes;
      if (
        requestedStep === BookingStep.ConfirmPhoneNumber &&
        !!reservation &&
        isTelehealthConsult
      )
        return BookingStep.ConfirmPhoneNumber;
    }

    if (requestedStep >= BookingStep.CompleteProfile) {
      if (isUserProfileComplete) return BookingStep.SelectPatient;
      if (isUserProfileComplete === false) return BookingStep.CompleteProfile;

      // The user is not logged in, continue to select patient to prompt for login
      return BookingStep.SelectPatient;
    }

    if (requestedStep >= BookingStep.SelectPatient && !!reservation) {
      // User has logged in but not completed their profile
      return isUserProfileComplete !== false
        ? BookingStep.SelectPatient
        : BookingStep.CompleteProfile;
    }

    if (requestedStep === BookingStep.SelectAppointmentType) {
      return BookingStep.SelectAppointmentType;
    }

    if (requestedStep === BookingStep.AppointmentTypeInformation) {
      return BookingStep.AppointmentTypeInformation;
    }

    if (requestedStep === BookingStep.SelectAppointmentSlot) {
      return BookingStep.SelectAppointmentSlot;
    }

    return BookingStep.SelectPatientType;
  }

  public previousStep(options: BookingWizardStepOptions): BookingStep {
    // Don't let them go back after confirming. In theory this will never be called because we don't show a
    // "back" button on this page.
    if (options.step === BookingStep.ConfirmBooking)
      return this.getConfigurationBasedStep(
        Move.Backward,
        BookingStep.SelectPatient,
        options
      );
    if (options.step === BookingStep.AddPatientNotes && options.reservation)
      return options.isTelehealthConsult
        ? BookingStep.ConfirmPhoneNumber
        : BookingStep.SelectPatient;

    if (options.step === BookingStep.ConfirmPhoneNumber)
      return BookingStep.SelectPatient;
    if (options.step === BookingStep.AddDependant)
      return BookingStep.SelectPatient;
    if (options.step === BookingStep.SelectPatient)
      return BookingStep.SelectAppointmentSlot;
    if (options.step === BookingStep.AppointmentTypeInformation)
      return BookingStep.SelectAppointmentType;
    if (options.step === BookingStep.SelectAppointmentSlot) {
      return options.hasAppointmentTypeInformation
        ? BookingStep.AppointmentTypeInformation
        : BookingStep.SelectAppointmentType;
    }
    if (options.step === BookingStep.CompleteProfile) {
      return BookingStep.SelectAppointmentSlot;
    }

    return BookingStep.SelectPatientType;
  }

  private getConfigurationBasedStep(
    move: Move,
    defaultTo: BookingStep,
    options: BookingWizardStepOptions
  ) {
    const { appointmentTypeId } = options.reservation || {};
    const { Forward, Backward } = Move;
    if (!appointmentTypeId) return BookingStep.SelectAppointmentType;

    let step = defaultTo;
    switch (move) {
      case Forward:
        if (options.isTelehealthConsult) step = BookingStep.ConfirmPhoneNumber;
        else if (options.isPatientNotesEnabled)
          step = BookingStep.AddPatientNotes;
        break;
      case Backward:
        if (options.isPatientNotesEnabled) step = BookingStep.AddPatientNotes;
        else if (options.isTelehealthConsult)
          step = BookingStep.ConfirmPhoneNumber;
        break;
    }
    return step;
  }

  public nextStep(options: BookingWizardStepOptions): BookingStep {
    if (options.isUserProfileComplete) {
      if (options.step === BookingStep.ConfirmBooking)
        return BookingStep.BookingSuccess;
      if (
        options.step === BookingStep.AddDependant &&
        options.reservation &&
        options.dependantId
      )
        return this.getConfigurationBasedStep(
          Move.Forward,
          BookingStep.ConfirmBooking,
          options
        );

      if (options.step === BookingStep.AddPatientNotes && options.reservation) {
        return BookingStep.ConfirmBooking;
      }
      if (
        options.step === BookingStep.ConfirmPhoneNumber &&
        options.reservation
      ) {
        return options.isPatientNotesEnabled
          ? BookingStep.AddPatientNotes
          : BookingStep.ConfirmBooking;
      }
      if (options.step === BookingStep.SelectPatient && options.reservation) {
        // Next screen could be confirm booking or add patient notes or confirm phone number
        const nextScreen = this.getConfigurationBasedStep(
          Move.Forward,
          BookingStep.ConfirmBooking,
          options
        );

        return options.isAppointmentForUser || options.dependantId
          ? nextScreen
          : BookingStep.AddDependant;
      }

      if (
        options.step === BookingStep.SelectAppointmentSlot &&
        options.reservation?.id
      ) {
        return options.isUserProfileComplete
          ? BookingStep.SelectPatient
          : BookingStep.CompleteProfile;
      }
    }

    if (options.step === BookingStep.CompleteProfile) {
      return this.calculateLogicalStep(BookingStep.ConfirmBooking, options);
    }

    if (
      options.step >= BookingStep.SelectAppointmentSlot &&
      !options.isUserProfileComplete
    )
      return BookingStep.CompleteProfile;

    if (options.step === BookingStep.SelectAppointmentType) {
      return options.hasAppointmentTypeInformation
        ? BookingStep.AppointmentTypeInformation
        : BookingStep.SelectAppointmentSlot;
    }

    if (options.step === BookingStep.AppointmentTypeInformation) {
      return BookingStep.SelectAppointmentSlot;
    }

    if (options.step === BookingStep.SelectPatientType) {
      return BookingStep.SelectAppointmentType;
    }

    return BookingStep.SelectPatientType;
  }
}
